import React, { useContext, useEffect, useRef, useState } from "react";
import { Bot, FileText, Search } from "lucide-react";
import { useTranslation } from "react-i18next";
import UsageLimitSurveyModal from "../../../components/UsageLimitSurveyModal";
import { AuthContext } from "../../auth/context/AuthContext";
import { useUsage } from "../../../contexts/UsageContext";
import { useSearchSession } from "../hooks/useSearchSession";
import { useSearchEngine } from "../hooks/useSearchEngine";
import SearchAppLayout from "../components/SearchAppLayout";
import SearchInputDock from "../components/SearchInputDock";
import ImageLightbox from "../components/ImageLightbox";
import PaperAnalysisPanel from "../components/PaperAnalysisPanel";
import SearchProgressIndicator from "../components/SearchProgressIndicator";
import DiscoveredPapers from "../components/DiscoveredPapers";
import MessageList from "../../chat/components/MessageList";
import {
  fetchSearchHistoryDetails,
  buildHistoryMessages,
} from "../services/searchHistoryService";
import { normalizeHistorySearchMode } from "../utils/historySearchMetadata";
import { fetchSearchModeSettings } from "../services/searchSettingsService";
import { getPaperStableId } from "../../../shared/utils/paper";
import { shouldSubmitOnEnter } from "../../../shared/utils/keyboard";
import { PaperProvider, usePaperContext } from "../context/PaperContext";
import { getSearchProgressStageLabelKey } from "../utils/searchProgressDisplay";
import { getSearchDockVisibility } from "../utils/searchDockVisibility";
import {
  buildSearchHistoryOpenEngagementEvent,
  enqueueResultEngagementEvent,
} from "../../../services/resultEngagementService";

function SearchLoadingIndicator({ message }) {
  const { t } = useTranslation();

  return (
    <div className="flex justify-start animate-fadeIn">
      <div className="w-full max-w-3xl">
        <div className="flex items-start gap-3">
          <div className="mt-1 h-8 w-8 rounded-full bg-blue-100 border border-blue-200 flex items-center justify-center flex-shrink-0">
            <Bot className="h-4 w-4 text-blue-600 animate-pulse" />
          </div>
          <div className="ai-message-enter relative flex-1 rounded-2xl rounded-tl-md border border-blue-100 bg-white px-5 py-4 sm:px-6 sm:py-5 shadow-sm">
            <span className="absolute -left-1.5 top-4 h-3 w-3 rotate-45 border-l border-b border-blue-100 bg-white" />
            <p className="text-sm sm:text-base text-gray-700 font-medium leading-relaxed">
              {message || t("app.searching")}
            </p>
            <div className="mt-2 flex items-center gap-1.5">
              <span className="ai-typing-dot h-1.5 w-1.5 rounded-full bg-blue-400" />
              <span
                className="ai-typing-dot h-1.5 w-1.5 rounded-full bg-blue-400"
                style={{ animationDelay: "0.2s" }}
              />
              <span
                className="ai-typing-dot h-1.5 w-1.5 rounded-full bg-blue-400"
                style={{ animationDelay: "0.4s" }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function CriteriaConfirmationWaitingIndicator({ message }) {
  return (
    <div className="flex justify-start animate-fadeIn">
      <div className="w-full max-w-3xl">
        <div className="flex items-start gap-3">
          <div className="mt-1 h-8 w-8 rounded-full bg-blue-100 border border-blue-200 flex items-center justify-center flex-shrink-0">
            <Bot className="h-4 w-4 text-blue-600 animate-pulse" />
          </div>
          <div className="ai-message-enter relative flex-1 rounded-2xl rounded-tl-md border border-blue-100 bg-white px-4 py-3 sm:px-5 sm:py-4 shadow-sm">
            <span className="absolute -left-1.5 top-4 h-3 w-3 rotate-45 border-l border-b border-blue-100 bg-white" />
            <p className="text-sm sm:text-base text-gray-700 font-medium leading-relaxed">
              {message}
            </p>
            <div className="mt-2 flex items-center gap-1.5">
              <span className="ai-typing-dot h-1.5 w-1.5 rounded-full bg-blue-400" />
              <span
                className="ai-typing-dot h-1.5 w-1.5 rounded-full bg-blue-400"
                style={{ animationDelay: "0.2s" }}
              />
              <span
                className="ai-typing-dot h-1.5 w-1.5 rounded-full bg-blue-400"
                style={{ animationDelay: "0.4s" }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function LiteratureSearchHeader({
  currentSearchId,
  onOpenPaperAnalysis,
  t,
  className = "",
}) {
  return (
    <div
      className={`flex items-start justify-between gap-3 border-b border-slate-200 bg-slate-50 px-4 py-3 ${className}`}
    >
      <div className="min-w-0">
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-700">
            <Search className="h-4 w-4" />
          </div>
          <h2 className="truncate text-sm font-semibold text-slate-950">
            {t("paperAnalysis.sources")}
          </h2>
        </div>
      </div>
      <button
        type="button"
        onClick={onOpenPaperAnalysis}
        disabled={!currentSearchId}
        className={`inline-flex flex-shrink-0 items-center gap-2 rounded-lg border px-3 py-2 text-xs font-semibold transition-colors ${
          currentSearchId
            ? "border-blue-200 bg-blue-50 text-blue-700 hover:border-blue-300 hover:bg-blue-100"
            : "cursor-not-allowed border-slate-200 bg-slate-50 text-slate-400"
        }`}
        title={t("paperAnalysis.open")}
      >
        <FileText className="h-4 w-4" />
        {t("paperAnalysis.open")}
      </button>
    </div>
  );
}

const isClient = typeof window !== "undefined";
const PAPER_ANALYSIS_DEFAULT_SPLIT_PERCENT = 42;
const PAPER_ANALYSIS_MIN_SPLIT_PERCENT = 30;
const PAPER_ANALYSIS_MAX_SPLIT_PERCENT = 70;
const FIRST_PAPER_SOURCE_SCROLL_OFFSET_PX = 16;
const WORKFLOW_PROMPT_TYPES = new Set([
  "purpose_collection",
  "purpose_clarification",
  "criteria_confirmation",
  "query_clarification",
]);

const clampPaperAnalysisSplit = (value) =>
  Math.min(
    PAPER_ANALYSIS_MAX_SPLIT_PERCENT,
    Math.max(PAPER_ANALYSIS_MIN_SPLIT_PERCENT, value),
  );

const getInitialIsMobile = () => (isClient ? window.innerWidth < 768 : false);

const getInitialSidebarVisible = (hasUser) => {
  if (!isClient || !hasUser) return false;
  if (window.innerWidth < 768) return false;

  const saved = localStorage.getItem("pubmed-search-sidebar-visible");
  return saved !== null ? saved === "true" : true;
};

function SearchWorkspaceContent() {
  const { user } = useContext(AuthContext);
  const { i18n, t } = useTranslation();
  const { updateUsage, hasSubmittedSurvey } = useUsage();
  const {
    contextPaperList,
    setContextPapers,
    clearContextPapers,
    removeContextPaper,
  } = usePaperContext();

  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);
  const [hasGlobalSearched, setHasGlobalSearched] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [searchSubmitNudgeActive, setSearchSubmitNudgeActive] = useState(false);
  const [searchModeSettings, setSearchModeSettings] = useState(null);
  const [showSidebar, setShowSidebar] = useState(() =>
    getInitialSidebarVisible(Boolean(user)),
  );
  const [isMobile, setIsMobile] = useState(getInitialIsMobile);
  const [paperAnalysisSplitPercent, setPaperAnalysisSplitPercent] = useState(
    PAPER_ANALYSIS_DEFAULT_SPLIT_PERCENT,
  );
  const [isPaperAnalysisResizing, setIsPaperAnalysisResizing] = useState(false);
  const [paperAnalysisMobileTab, setPaperAnalysisMobileTab] = useState("chat");
  const [isPaperAnalysisOpen, setIsPaperAnalysisOpen] = useState(false);
  const [sourceSelectionNudgeKey, setSourceSelectionNudgeKey] = useState(0);

  const messagesEndRef = useRef(null);
  const mainScrollRef = useRef(null);
  const inputDockRef = useRef(null);
  const paperAnalysisSplitRef = useRef(null);
  const paperAnalysisPanelRef = useRef(null);
  const previousSearchIdRef = useRef(null);
  const paperAnalysisGuidanceLoadRequestedRef = useRef(new Set());

  const {
    messages,
    setMessages,
    currentSearchId,
    setCurrentSearchId,
    isHistoryView,
    setIsHistoryView,
    setHasSearchedInSession,
    handleStartNewSession,
  } = useSearchSession();
  const prevMessagesLengthRef = useRef(messages.length);

  const scrollMessagesToBottom = React.useCallback(() => {
    const scrollToBottom = (behavior = "smooth") => {
      const mainElement = mainScrollRef.current;

      if (mainElement && typeof mainElement.scrollTo === "function") {
        mainElement.scrollTo({ top: mainElement.scrollHeight, behavior });
        return;
      }

      messagesEndRef.current?.scrollIntoView({ behavior, block: "end" });
    };

    window.requestAnimationFrame(() => {
      scrollToBottom("smooth");
      window.setTimeout(() => scrollToBottom("auto"), 120);
      window.setTimeout(() => scrollToBottom("auto"), 320);
    });
  }, []);

  const {
    query,
    setQuery,
    progress,
    isAwaitingCriteriaConfirmation,
    isAwaitingUserPurpose,
    workflowWaitingContext,
    discoveredPapers,
    paperAnalysisGuidance,
    loadPersistedPaperAnalysisGuidance,
    handleSearch,
    submitSearchPurpose,
    submitCriteriaConfirmation,
    submitQueryClarification,
    resetProgress,
    resetSearchState,
    showSurveyModal,
    setShowSurveyModal,
    searchMode,
    setSearchMode,
  } = useSearchEngine({
    setMessages,
    setCurrentSearchId,
    setHasSearchedInSession,
    updateUsage,
    hasSubmittedSurvey,
    onSearchComplete: () => {
      setHistoryRefreshKey((prev) => prev + 1);
    },
  });

  useEffect(() => {
    if (!currentSearchId && messages.length <= 1) {
      setHasGlobalSearched(false);
    }
  }, [currentSearchId, messages]);

  useEffect(() => {
    if (hasGlobalSearched) return;

    const hasExistingSearchContext =
      Boolean(currentSearchId) ||
      messages.some((message) =>
        [
          "result",
          "criteria_confirmation",
          "purpose_collection",
          "purpose_clarification",
          "query_clarification",
          "ai_overview",
          "ai_overview_stream",
        ].includes(
          message.type,
        ),
      );

    if (hasExistingSearchContext) {
      setHasGlobalSearched(true);
    }
  }, [currentSearchId, hasGlobalSearched, messages]);

  useEffect(() => {
    let cancelled = false;

    const loadSearchModeSettings = async () => {
      try {
        const settings = await fetchSearchModeSettings();
        if (!cancelled) {
          setSearchModeSettings(settings);
        }
      } catch (error) {
        console.error("Failed to load search mode settings:", error);
      }
    };

    loadSearchModeSettings();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const htmlElement = document.documentElement;
    htmlElement.classList.remove("lang-en", "lang-ja", "lang-zh");
    htmlElement.classList.add(`lang-${i18n.language}`);

    const handleLanguageChanged = () => {
      htmlElement.classList.remove("lang-en", "lang-ja", "lang-zh");
      htmlElement.classList.add(`lang-${i18n.language}`);
    };

    i18n.on("languageChanged", handleLanguageChanged);
    return () => i18n.off("languageChanged", handleLanguageChanged);
  }, [i18n]);

  useEffect(() => {
    const handleResize = () => {
      const mobile = getInitialIsMobile();
      setIsMobile(mobile);
      if (mobile && showSidebar) {
        setShowSidebar(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [showSidebar]);

  useEffect(() => {
    if (user && !isMobile) {
      localStorage.setItem("pubmed-search-sidebar-visible", showSidebar.toString());
    }
  }, [showSidebar, user, isMobile]);

  useEffect(() => {
    if (!searchSubmitNudgeActive) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setSearchSubmitNudgeActive(false);
    }, 3200);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [searchSubmitNudgeActive]);

  useEffect(() => {
    if (sourceSelectionNudgeKey && contextPaperList.length > 0) {
      setSourceSelectionNudgeKey(0);
    }
  }, [contextPaperList.length, sourceSelectionNudgeKey]);

  useEffect(() => {
    const previousMessagesLength = prevMessagesLengthRef.current;
    const lengthDelta = messages.length - previousMessagesLength;
    const lengthChanged = lengthDelta > 0;
    const latestMessage = messages[messages.length - 1];

    if (lengthChanged) {
      const isBulkHistoryLoad = isHistoryView && lengthDelta > 1;
      const skipAutoScrollTypes = new Set(["ai_overview_stream", "ai_overview"]);
      if (isBulkHistoryLoad) {
        // Avoid jumping when loading a history thread with many existing messages.
      } else if (skipAutoScrollTypes.has(latestMessage?.type)) {
        // Keep viewport stable while overview is streaming/completing.
      } else if (latestMessage?.type === "result") {
        setTimeout(() => {
          const resultsElements = document.querySelectorAll(
            "[data-results-dashboard='true'], .results-split",
          );
          if (resultsElements.length > 0) {
            resultsElements[0].scrollIntoView({
              behavior: "smooth",
              block: "start",
            });
          }
        }, 100);
      } else {
        scrollMessagesToBottom();
      }
    }

    prevMessagesLengthRef.current = messages.length;
  }, [messages, isHistoryView, scrollMessagesToBottom]);

  const submitSearch = () => {
    setSearchSubmitNudgeActive(false);
    if (isHistoryView) {
      return;
    }

    handleSearch(null);
  };

  const handleQueryKeyDown = (event) => {
    if (shouldSubmitOnEnter(event)) {
      event.preventDefault();
      submitSearch();
    }
  };

  const handleRerunInPro = (searchQuery) => {
    if (!searchQuery || !searchQuery.trim()) {
      return;
    }

    resetSearchState();
    setIsHistoryView(false);
    setHasGlobalSearched(false);
    clearContextPapers();
    handleStartNewSession();
    setSearchMode("pro");
    setQuery(searchQuery);
    setSearchSubmitNudgeActive(true);
  };

  const scrollToPaperById = (paperId) => {
    if (!paperId) return;

    const row = document.getElementById(`paper-row-${paperId}`);
    const card =
      row ||
      Array.from(document.querySelectorAll("[data-paper-card-id]")).find(
        (element) => element.dataset.paperCardId === String(paperId),
      );

    if (card) {
      card.scrollIntoView({ behavior: "smooth", block: "center" });
      card.classList.add("highlight-flash");
      setTimeout(() => {
        card.classList.remove("highlight-flash");
      }, 2000);
    }
  };

  const scrollToFirstPaperSourceControl = React.useCallback(() => {
    const firstPaperCard =
      document.querySelector("[data-first-paper='true']") ||
      document.querySelector("[data-paper-card-id]");
    if (!firstPaperCard) return;

    const scrollContainer =
      mainScrollRef.current ||
      document.querySelector("[data-search-scroll-container='true']");

    if (scrollContainer && typeof scrollContainer.scrollTo === "function") {
      const containerRect = scrollContainer.getBoundingClientRect();
      const cardRect = firstPaperCard.getBoundingClientRect();
      scrollContainer.scrollTo({
        top:
          scrollContainer.scrollTop +
          cardRect.top -
          containerRect.top -
          FIRST_PAPER_SOURCE_SCROLL_OFFSET_PX,
        behavior: "smooth",
      });
    } else {
      firstPaperCard.scrollIntoView({ behavior: "smooth", block: "start" });
    }

    firstPaperCard.classList.add("highlight-flash");
    window.setTimeout(() => {
      firstPaperCard.classList.remove("highlight-flash");
    }, 2000);

    const sourceButton = firstPaperCard.querySelector("[data-context-button='true']");
    if (sourceButton && typeof sourceButton.focus === "function") {
      window.setTimeout(() => {
        sourceButton.focus({ preventScroll: true });
      }, 420);
    }
  }, []);

  const handleSourceSelectionRequired = React.useCallback(() => {
    setIsPaperAnalysisOpen(true);
    setPaperAnalysisMobileTab("sources");
    setSourceSelectionNudgeKey(Date.now());

    window.requestAnimationFrame(() => {
      window.setTimeout(scrollToFirstPaperSourceControl, 80);
    });
  }, [scrollToFirstPaperSourceControl]);

  const handleReferenceClick = (index, papers) => {
    if (!papers || !papers[index]) return;

    const paper = papers[index];
    scrollToPaperById(getPaperStableId(paper, index));
  };

  const hasHistoryAnalysisProgress =
    isHistoryView &&
    (query.trim().length > 0 ||
      contextPaperList.length > 0);

  const hasActiveWorkflowPrompt = messages.some((message) => {
    if (!WORKFLOW_PROMPT_TYPES.has(message.type)) {
      return false;
    }
    if (
      workflowWaitingContext === "criteria_confirmation" &&
      message.type === "purpose_clarification"
    ) {
      return false;
    }
    return !Boolean(message.locked);
  });
  const showWorkflowWaitingIndicator =
    isAwaitingCriteriaConfirmation && !hasActiveWorkflowPrompt && searchMode !== "basic";
  const showInitialSearchLoadingIndicator =
    progress.isSearching &&
    !progress.currentStage &&
    !discoveredPapers &&
    !hasActiveWorkflowPrompt &&
    !showWorkflowWaitingIndicator;
  const workflowWaitingMessage = (() => {
    if (workflowWaitingContext === "purpose_collection") {
      return t("app.purposePrompt.waitingQuestion");
    }
    if (workflowWaitingContext === "purpose_clarification") {
      return t("app.purposePrompt.waitingClarification");
    }
    if (workflowWaitingContext === "criteria_confirmation") {
      return t("app.criteriaConfirmation.detecting");
    }
    if (workflowWaitingContext === "query_clarification") {
      return t("app.queryPrompt.waiting");
    }
    if (isAwaitingUserPurpose) {
      return t("app.purposePrompt.waitingClarification");
    }
    return t("app.criteriaConfirmation.detecting");
  })();

  const confirmHistoryExit = () => {
    if (!hasHistoryAnalysisProgress) return true;
    return window.confirm(t("app.historyExitWarning"));
  };

  const handleLoadSearchFromHistory = async (search) => {
    if (
      isHistoryView &&
      search?.id &&
      search.id !== currentSearchId &&
      !confirmHistoryExit()
    ) {
      return;
    }

    try {
      const searchDetails = await fetchSearchHistoryDetails(search.id);
      enqueueResultEngagementEvent(
        buildSearchHistoryOpenEngagementEvent({
          searchDetails: {
            ...searchDetails,
            id: searchDetails.id || searchDetails.search_id || search.id,
          },
          surface: "search_sidebar",
          entryPoint: "left_sidebar",
        }),
      );
      const historySearchMode = normalizeHistorySearchMode(
        searchDetails.search_mode,
      );

      setCurrentSearchId(search.id);
      setQuery("");
      if (historySearchMode) {
        setSearchMode(historySearchMode);
      }

      resetProgress();
      setContextPapers(new Map());
      setIsHistoryView(true);

      const loadedMessages = buildHistoryMessages({
        searchDetails,
        loadedSummary: t("app.loadedPapersFromHistory", {
          count: searchDetails.papers.length,
        }),
        criteriaSummaryMessage: t("app.criteriaConfirmation.finalizedSummary"),
      });

      setMessages(loadedMessages);
      setHasSearchedInSession(true);
      setHasGlobalSearched(true);
      paperAnalysisGuidanceLoadRequestedRef.current.add(search.id);
      loadPersistedPaperAnalysisGuidance(search.id).catch((error) => {
        console.error("Failed to load persisted paper analysis guidance:", error);
      });

      if (isMobile) {
        setShowSidebar(false);
      }
    } catch (error) {
      console.error("Failed to load search from history:", error);
      window.alert(t("app.errors.historyLoadFailed"));
    }
  };

  const resetSession = () => {
    if (isHistoryView && !confirmHistoryExit()) {
      return;
    }

    resetSearchState();
    setIsHistoryView(false);
    setQuery("");
    setHasGlobalSearched(false);
    paperAnalysisGuidanceLoadRequestedRef.current = new Set();
    clearContextPapers();
    handleStartNewSession();
  };

  const latestResultMessage = [...messages]
    .reverse()
    .find((message) => message.type === "result" && (message.papers || []).length > 0);
  const latestOverviewStreamMessage = [...messages]
    .reverse()
    .find((message) => message.type === "ai_overview_stream");
  const latestOverviewMessage = [...messages]
    .reverse()
    .find((message) => message.type === "ai_overview");
  const hasPaperAnalysisWorkspace = Boolean(
    currentSearchId &&
      (latestResultMessage ||
        latestOverviewStreamMessage ||
        latestOverviewMessage ||
        progress.isSearching),
  );
  const showPaperAnalysisWorkspace = hasPaperAnalysisWorkspace && isPaperAnalysisOpen;
  const currentSearchQuery =
    latestResultMessage?.query ||
    messages.find((message) => message.type === "user")?.content ||
    "";
  const currentOverviewData =
    latestOverviewMessage?.data ||
    latestResultMessage?.ai_overview ||
    null;
  const currentOverviewStreamContent = latestOverviewStreamMessage?.content || "";
  const isOverviewGenerating = Boolean(latestOverviewStreamMessage) ||
    Boolean(showPaperAnalysisWorkspace && progress.isSearching && !latestOverviewMessage);
  const overviewStatusLabelKey = getSearchProgressStageLabelKey(progress);
  const overviewStatusLabel = overviewStatusLabelKey
    ? t(overviewStatusLabelKey)
    : t("app.aiOverview.loading");
  const { showInitialSearchDock, showPersistentSearchDock } =
    getSearchDockVisibility({
      isHistoryView,
      hasGlobalSearched,
      isSearching: progress.isSearching,
      currentSearchId,
      messages,
    });
  const shouldShowLiteratureSearchHeader =
    !showPaperAnalysisWorkspace && hasPaperAnalysisWorkspace;

  useEffect(() => {
    if (currentSearchId && currentSearchId !== previousSearchIdRef.current) {
      previousSearchIdRef.current = currentSearchId;
      setIsPaperAnalysisOpen(true);
      setPaperAnalysisMobileTab("chat");
    }
  }, [currentSearchId]);

  useEffect(() => {
    if (
      !currentSearchId ||
      !latestResultMessage ||
      paperAnalysisGuidance ||
      progress.isSearching ||
      paperAnalysisGuidanceLoadRequestedRef.current.has(currentSearchId)
    ) {
      return;
    }

    paperAnalysisGuidanceLoadRequestedRef.current.add(currentSearchId);
    loadPersistedPaperAnalysisGuidance(currentSearchId).catch((error) => {
      console.error("Failed to load persisted paper analysis guidance:", error);
    });
  }, [
    currentSearchId,
    latestResultMessage,
    loadPersistedPaperAnalysisGuidance,
    paperAnalysisGuidance,
    progress.isSearching,
  ]);

  const updatePaperAnalysisSplitFromPointer = React.useCallback((clientX) => {
    const splitElement = paperAnalysisSplitRef.current;
    if (!splitElement) return;

    const splitRect = splitElement.getBoundingClientRect();
    const nextPercent = ((clientX - splitRect.left) / splitRect.width) * 100;
    setPaperAnalysisSplitPercent(clampPaperAnalysisSplit(nextPercent));
  }, []);

  const handlePaperAnalysisDividerPointerDown = React.useCallback(
    (event) => {
      if (isMobile) return;

      event.preventDefault();
      updatePaperAnalysisSplitFromPointer(event.clientX);
      setIsPaperAnalysisResizing(true);

      const previousCursor = document.body.style.cursor;
      const previousUserSelect = document.body.style.userSelect;
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";

      const handlePointerMove = (moveEvent) => {
        updatePaperAnalysisSplitFromPointer(moveEvent.clientX);
      };
      const handlePointerUp = () => {
        setIsPaperAnalysisResizing(false);
        document.body.style.cursor = previousCursor;
        document.body.style.userSelect = previousUserSelect;
        window.removeEventListener("pointermove", handlePointerMove);
        window.removeEventListener("pointerup", handlePointerUp);
      };

      window.addEventListener("pointermove", handlePointerMove);
      window.addEventListener("pointerup", handlePointerUp);
    },
    [isMobile, updatePaperAnalysisSplitFromPointer],
  );

  const handlePaperAnalysisDividerKeyDown = React.useCallback((event) => {
    if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) {
      return;
    }

    event.preventDefault();
    setPaperAnalysisSplitPercent((prev) => {
      if (event.key === "Home") return PAPER_ANALYSIS_MIN_SPLIT_PERCENT;
      if (event.key === "End") return PAPER_ANALYSIS_MAX_SPLIT_PERCENT;
      const delta = event.key === "ArrowLeft" ? -5 : 5;
      return clampPaperAnalysisSplit(prev + delta);
    });
  }, []);

  const paperAnalysisSplitStyle = showPaperAnalysisWorkspace
    ? {
        gridTemplateColumns: `calc(${paperAnalysisSplitPercent}% - 6px) 12px calc(${
          100 - paperAnalysisSplitPercent
        }% - 6px)`,
      }
    : undefined;
  const hiddenSearchMessageTypes = [
    "ai_overview",
    "ai_overview_stream",
    ...(showPaperAnalysisWorkspace ? ["criteria_confirmation"] : []),
  ];
  const sourceSelectionNudgeActive =
    Boolean(sourceSelectionNudgeKey) && contextPaperList.length === 0;

  const searchConversationContent = (
    <>
      <MessageList
        messages={messages}
        onRerunInPro={handleRerunInPro}
        onPurposePromptSubmit={submitSearchPurpose}
        onCriteriaConfirmationSubmit={submitCriteriaConfirmation}
        onQueryClarificationSubmit={submitQueryClarification}
        onReferenceClick={handleReferenceClick}
        onSelectImage={setSelectedImage}
        isHistoryView={isHistoryView}
        currentSearchId={currentSearchId}
        hiddenMessageTypes={hiddenSearchMessageTypes}
        onOpenPaperAnalysis={() => setIsPaperAnalysisOpen(true)}
        searchMode={searchMode}
        onSearchModeChange={setSearchMode}
        sourceSelectionNudgeKey={
          sourceSelectionNudgeActive ? sourceSelectionNudgeKey : 0
        }
        sourceSelectionNudgeTargetResultId={latestResultMessage?.id || null}
      />

      {showWorkflowWaitingIndicator && (
        <CriteriaConfirmationWaitingIndicator
          message={workflowWaitingMessage}
        />
      )}

      {showInitialSearchLoadingIndicator && (
        <SearchLoadingIndicator message={t("app.searchLoading.initial")} />
      )}

      <div ref={messagesEndRef} />

      {((progress.isSearching && progress.currentStage) || discoveredPapers) && (
        <div className="space-y-6 animate-fadeIn">
          <SearchProgressIndicator progress={progress} />
          {discoveredPapers && (
            <DiscoveredPapers
              papers={discoveredPapers.papers}
              message={discoveredPapers.message}
            />
          )}
        </div>
      )}

    </>
  );

  const handleSearchQueryChange = (nextQuery) => {
    setQuery(nextQuery);
    if (searchSubmitNudgeActive) {
      setSearchSubmitNudgeActive(false);
    }
  };

  const commonSearchInputProps = {
    containerRef: inputDockRef,
    showSidebar,
    isMobile,
    isHistoryView,
    hasGlobalSearched,
    isSearching: progress.isSearching || isAwaitingUserPurpose,
    query,
    searchMode,
    onSearchModeChange: setSearchMode,
    onQueryChange: handleSearchQueryChange,
    onQueryKeyDown: handleQueryKeyDown,
    onSubmit: submitSearch,
    searchSubmitNudgeActive,
    searchModeSettings,
  };

  const initialSearchInput = showInitialSearchDock ? (
    <SearchInputDock
      {...commonSearchInputProps}
      placement="inline"
      placeholder={t("app.welcome.guidance")}
    />
  ) : null;

  const fixedPersistentSearchInput =
    showPersistentSearchDock && !showPaperAnalysisWorkspace ? (
      <SearchInputDock {...commonSearchInputProps} showDisclaimer={false} />
    ) : null;

  const paperAnalysisSearchInput =
    showPersistentSearchDock && showPaperAnalysisWorkspace ? (
      <div className="flex-shrink-0 border-b border-slate-200 bg-white px-4 py-3 sm:px-6 lg:col-span-3 lg:rounded-2xl lg:border lg:border-slate-200/80 lg:shadow-sm">
        <SearchInputDock
          {...commonSearchInputProps}
          placement="inline"
          showDisclaimer={false}
        />
      </div>
    ) : null;

  const mainStyle = (() => {
    if (showInitialSearchDock) {
      return undefined;
    }

    if (showPersistentSearchDock && !showPaperAnalysisWorkspace) {
      return { paddingBottom: "132px" };
    }

    if (!showPaperAnalysisWorkspace) {
      return { paddingBottom: "48px" };
    }

    return undefined;
  })();

  const paperAnalysisMobileTabs = [
    { id: "sources", label: t("paperAnalysis.sources") },
    { id: "chat", label: t("paperAnalysis.chat") },
  ];

  return (
    <SearchAppLayout
      user={user}
      showSidebar={showSidebar}
      setShowSidebar={setShowSidebar}
      onLoadSearch={handleLoadSearchFromHistory}
      onNewSession={resetSession}
      isMobile={isMobile}
      currentSearchId={currentSearchId}
      historyRefreshKey={historyRefreshKey}
    >
      <main
        ref={showPaperAnalysisWorkspace ? null : mainScrollRef}
        data-search-scroll-container="true"
        className={
          showPaperAnalysisWorkspace
            ? "relative flex-1 overflow-y-auto bg-white lg:overflow-hidden lg:bg-[#e8ebf7] lg:p-4"
            : showInitialSearchDock
              ? "relative flex-1 overflow-hidden bg-gray-50"
              : "relative flex-1 overflow-y-auto bg-gray-50"
        }
        style={mainStyle}
      >
        {showPaperAnalysisWorkspace ? (
          <div
            ref={paperAnalysisSplitRef}
            className={`relative mx-auto flex min-h-full max-w-none flex-col lg:grid lg:h-full lg:gap-0 ${
              paperAnalysisSearchInput ? "lg:grid-rows-[auto_minmax(0,1fr)]" : ""
            }`}
            style={paperAnalysisSplitStyle}
          >
            {paperAnalysisSearchInput}

            <div
              role="tablist"
              aria-label={t("paperAnalysis.mobileTabs")}
              className="sticky top-0 z-30 grid h-12 flex-shrink-0 grid-cols-2 border-b border-slate-200 bg-white lg:hidden"
            >
              {paperAnalysisMobileTabs.map((tab) => {
                const isActive = paperAnalysisMobileTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    onClick={() => setPaperAnalysisMobileTab(tab.id)}
                    className={`relative flex items-center justify-center text-sm font-medium transition-colors ${
                      isActive ? "text-slate-950" : "text-slate-500"
                    }`}
                  >
                    {tab.label}
                    <span
                      className={`absolute bottom-0 h-0.5 w-14 rounded-full transition-colors ${
                        isActive ? "bg-blue-600" : "bg-transparent"
                      }`}
                    />
                  </button>
                );
              })}
            </div>

            <section
              className={`relative z-0 min-w-0 flex-col overflow-hidden bg-white lg:flex lg:h-full lg:rounded-2xl lg:border lg:border-slate-200/80 lg:shadow-sm ${
                paperAnalysisMobileTab === "sources" ? "flex" : "hidden"
              }`}
            >
              <LiteratureSearchHeader
                currentSearchId={currentSearchId}
                onOpenPaperAnalysis={() => setIsPaperAnalysisOpen(true)}
                t={t}
                className="hidden lg:flex"
              />
              <div
                ref={mainScrollRef}
                className="min-h-0 flex-1 overflow-visible lg:overflow-y-auto"
              >
                <div className="min-h-[36rem] min-w-0 space-y-6 px-4 py-5 sm:px-6 lg:px-7 lg:py-6">
                  {searchConversationContent}
                </div>
              </div>
            </section>

            <div
              role="separator"
              aria-orientation="vertical"
              aria-valuemin={PAPER_ANALYSIS_MIN_SPLIT_PERCENT}
              aria-valuemax={PAPER_ANALYSIS_MAX_SPLIT_PERCENT}
              aria-valuenow={Math.round(paperAnalysisSplitPercent)}
              tabIndex={0}
              onPointerDown={handlePaperAnalysisDividerPointerDown}
              onKeyDown={handlePaperAnalysisDividerKeyDown}
              className="group relative z-20 hidden cursor-col-resize touch-none select-none items-stretch justify-center outline-none lg:flex"
            >
              <div
                className={`my-2 w-px rounded-full transition-colors ${
                  isPaperAnalysisResizing
                    ? "bg-blue-500"
                    : "bg-transparent group-hover:bg-blue-400 group-focus-visible:bg-blue-400"
                }`}
              />
            </div>

            <section
              ref={paperAnalysisPanelRef}
              className={`relative z-0 min-w-0 overflow-hidden bg-white lg:block lg:h-full lg:rounded-2xl lg:border lg:border-slate-200/80 lg:shadow-sm ${
                paperAnalysisMobileTab === "chat" ? "block" : "hidden"
              }`}
            >
              <PaperAnalysisPanel
                papers={contextPaperList}
                availablePapers={latestResultMessage?.papers || []}
                parentSearchRunId={currentSearchId}
                originalQuery={currentSearchQuery}
                searchMode={searchMode}
                language={i18n.language}
                overviewData={currentOverviewData}
                overviewStreamContent={currentOverviewStreamContent}
                isOverviewGenerating={isOverviewGenerating}
                overviewStatusLabel={overviewStatusLabel}
                guidance={paperAnalysisGuidance}
                embedded
                onClose={() => setIsPaperAnalysisOpen(false)}
                onRemoveSource={removeContextPaper}
                onSourceLabelClick={scrollToPaperById}
                onSourceSelectionRequired={handleSourceSelectionRequired}
                showSourceSelectionNudge={sourceSelectionNudgeActive}
                onUsageUpdated={updateUsage}
              />
            </section>
          </div>
        ) : showInitialSearchDock ? (
          <div className="mx-auto h-full max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
            <div className="grid h-full grid-rows-[minmax(0,1fr)_auto_minmax(0,1fr)] gap-5">
              <div className="flex min-h-0 items-end justify-center overflow-visible pb-2">
                <div className="w-full">{searchConversationContent}</div>
              </div>
              <div>{initialSearchInput}</div>
              <div aria-hidden="true" />
            </div>
          </div>
        ) : (
          <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
            <div className="min-h-[36rem] min-w-0 space-y-6">
              {shouldShowLiteratureSearchHeader && (
                <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                  <LiteratureSearchHeader
                    currentSearchId={currentSearchId}
                    onOpenPaperAnalysis={() => setIsPaperAnalysisOpen(true)}
                    t={t}
                    className="border-b-0"
                  />
                </div>
              )}
              {searchConversationContent}
            </div>
          </div>
        )}
      </main>

      {fixedPersistentSearchInput}

      <UsageLimitSurveyModal
        isOpen={showSurveyModal}
        onClose={() => setShowSurveyModal(false)}
      />

      <ImageLightbox imageUrl={selectedImage} onClose={() => setSelectedImage(null)} />
    </SearchAppLayout>
  );
}

export default function SearchWorkspacePage() {
  return (
    <PaperProvider>
      <SearchWorkspaceContent />
    </PaperProvider>
  );
}
